import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, DollarSign, ShoppingBag, RotateCcw, CreditCard, Banknote, Smartphone } from "lucide-react";
import { format } from "date-fns";

export default function DailySalesReport() {
  const { getAuthHeader } = useAuth();
  const [dailyReport, setDailyReport] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const [dailyRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/reports/daily`, getAuthHeader()),
        axios.get(`${API}/reports/monthly-chart`, getAuthHeader())
      ]);
      setDailyReport(dailyRes.data);
      setMonthlyData(monthlyRes.data);
    } catch (error) {
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const getPaymentIcon = (method) => {
    switch (method) {
      case "Cash": return <Banknote className="w-5 h-5" />;
      case "Credit": return <CreditCard className="w-5 h-5" />;
      case "Debit": return <CreditCard className="w-5 h-5" />;
      case "Online": return <Smartphone className="w-5 h-5" />;
      default: return <DollarSign className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6" data-testid="daily-sales-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Daily Sales Report</h1>
          <p className="text-slate-500">
            {format(new Date(), "EEEE, MMMM dd, yyyy")}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">TOTAL SALES</p>
                <p className="text-3xl font-bold mt-1">
                  ${dailyReport?.total_amount?.toFixed(2) || "0.00"}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">TRANSACTIONS</p>
                <p className="text-3xl font-bold mt-1">{dailyReport?.total_sales || 0}</p>
              </div>
              <ShoppingBag className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">RETURNS</p>
                <p className="text-3xl font-bold mt-1">{dailyReport?.total_returns || 0}</p>
              </div>
              <RotateCcw className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">AVG TRANSACTION</p>
                <p className="text-3xl font-bold mt-1">
                  ${dailyReport?.total_sales > 0 
                    ? (dailyReport.total_amount / dailyReport.total_sales).toFixed(2) 
                    : "0.00"}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Breakdown */}
      {dailyReport?.payment_breakdown && Object.keys(dailyReport.payment_breakdown).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Payment Methods Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(dailyReport.payment_breakdown).map(([method, data]) => (
                <div key={method} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    {getPaymentIcon(method)}
                    <span className="font-semibold">{method}</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-700">{data.count}</p>
                  <p className="text-sm text-green-600 font-mono">${data.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Sales Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Monthly Sales Chart
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  formatter={(value) => [`$${value.toFixed(2)}`, "Sales"]}
                  contentStyle={{ 
                    backgroundColor: "#fff", 
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="sales" 
                  name="Sales ($)" 
                  fill="#22c55e" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
